// Health view — per-team RAG rollup. One row per team, three cells
// (rocks, todos, opps) plus an overall chip. The sparkline column the
// Driicut original ships is replaced with the current percentage
// figure until the daily snapshot job is wired up.
import { tpApi } from "../api";
import { useResource } from "../hooks";
import { ragClass } from "../utils";

export function HealthView({ scope }: { scope: "mine" | "all" }) {
  const health = useResource(() => tpApi.health(scope), [scope]);
  const rollup = health.data?.rollup ?? {
    teamsGreen: 0,
    teamsAmber: 0,
    teamsRed: 0,
    rocksAtRiskOrOff: 0,
    todosOverdue: 0,
    oppsStale: 0,
  };

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
        }}
      >
        <Tile label="Teams green" value={rollup.teamsGreen ?? 0} rag="green" />
        <Tile label="Teams amber" value={rollup.teamsAmber ?? 0} rag="amber" />
        <Tile label="Teams red" value={rollup.teamsRed ?? 0} rag="red" />
        <Tile label="Rocks at risk" value={rollup.rocksAtRiskOrOff ?? 0} rag="neutral" />
        <Tile label="Todos overdue" value={rollup.todosOverdue ?? 0} rag="neutral" />
        <Tile label="Opps stale" value={rollup.oppsStale ?? 0} rag="neutral" />
      </div>

      <div className="bsp-card" style={{ padding: 0, marginTop: 12 }}>
        <table className="bsp-table">
          <thead>
            <tr>
              <th>Team</th>
              <th>Rocks</th>
              <th>To-dos</th>
              <th>Opportunities</th>
              <th>Overall</th>
            </tr>
          </thead>
          <tbody>
            {(health.data?.teams ?? []).map((t) => (
              <tr key={t.team.id}>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 4,
                        background: "var(--bsp-blue-tint)",
                        color: "var(--bsp-blue)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      {t.team.mono}
                    </span>
                    <strong style={{ fontSize: 13 }}>{t.team.name}</strong>
                  </div>
                </td>
                <td>
                  <Cell row={t.rocks as RowData} rag={t.rocks.rag as Rag} suffix="on track" />
                </td>
                <td>
                  <Cell row={t.todos as RowData} rag={t.todos.rag as Rag} suffix="overdue" overdueKey />
                </td>
                <td>
                  <Cell row={t.opps as RowData} rag={t.opps.rag as Rag} suffix="stale" staleKey />
                </td>
                <td>
                  <span className={ragClass(t.overall)}>{t.overall}</span>
                </td>
              </tr>
            ))}
            {(health.data?.teams ?? []).length === 0 && !health.loading ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", color: "var(--bsp-fg-muted)" }}>
                  No teams to show.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}

type Rag = "green" | "amber" | "red" | "neutral";
type RowData = {
  total: number;
  green?: number;
  pctOnTrack?: number;
  overdue?: number;
  pctOverdue?: number;
  stale?: number;
  pctStale?: number;
};

function Tile({ label, value, rag }: { label: string; value: number; rag: Rag }) {
  return (
    <div className="tp-stat-tile">
      <div className="tp-stat-label">{label}</div>
      <div className={`tp-stat-value ${rag === "neutral" ? "tone-muted" : ""}`} style={rag === "green" ? { color: "var(--bsp-green)" } : rag === "amber" ? { color: "var(--bsp-orange)" } : rag === "red" ? { color: "var(--bsp-red)" } : undefined}>
        {value}
      </div>
    </div>
  );
}

function Cell({
  row,
  rag,
  suffix,
  overdueKey,
  staleKey,
}: {
  row: RowData;
  rag: Rag;
  suffix: string;
  overdueKey?: boolean;
  staleKey?: boolean;
}) {
  const focusValue = overdueKey
    ? row.overdue ?? 0
    : staleKey
      ? row.stale ?? 0
      : row.green ?? 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span className={ragClass(rag)}>{rag}</span>
      <span style={{ fontSize: 12, color: "var(--bsp-fg-muted)" }}>
        {focusValue} / {row.total} {suffix}
      </span>
    </div>
  );
}
